import { format } from "multi-date";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { DatePicker } from "react-responsive-calendar-picker";
import { toast, ToastContainer } from "react-toastify";

// component
import { Input } from "components/LoginAndRegister/Input";

import { axiosInstance } from "helpers/axiosInstance";

// assets
import { ReactComponent as Adult } from "assets/icons/adults.svg";
import { ReactComponent as Children } from "assets/icons/children.svg";
import { SvgClose } from "assets/icons/js/SvgClose";

// styles
import "./style.scss";

interface PageProps {
	closeModal: () => void;
	listingID: string;
}

export const LeadForm = ({ closeModal, listingID }: PageProps) => {
	const unlockMagicLeadGen = useMutation((data: any) => {
		return axiosInstance({
			method: "post",
			url: "/create-lead",
			headers: {
				"Content-Type": "application/json",
			},
			data: {
				VILLAS_IN_MIND: [data.listingID],
				GUEST_NAME: data.firstName + " " + data.lastName,
				GUEST_EMAIL: data.email,
				GUEST_NUMBER: data.mobileNumber,
				CHECK_IN: format(data.checkin, "YYYY-MM-DD"),
				CHECK_OUT: format(data.checkout, "YYYY-MM-DD"),
				NO_OF_GUESTS: data.adult,
				NO_OF_KIDS: data.children,
				MEDIUM: "Website - Book Now",
			},
		});
	});

	const [dates, setDates] = useState({
		checkin: "",
		checkout: "",
	});

	const [openCalenderCheckIn, setOpenCalendarCheckIn] = useState(false);
	const [openCalenderCheckOut, setOpenCalendarCheckOut] = useState(false);

	useEffect(() => {
		if (dates.checkin) {
			setOpenCalendarCheckIn(false);
			setOpenCalendarCheckOut(true);
		}
		if (dates.checkout) {
			setOpenCalendarCheckOut(false);
		}
	}, [dates]);

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [mobileNumber, setMobileNumber] = useState("");
	const [adult, setAdult] = useState(1);
	const [children, setChildren] = useState(0);

	const handleMobileChange = (e: any) => {
		const re = /^[0-9\b]+$/;
		if (e.target.value.length <= 10) {
			if (e.target.value === "" || re.test(e.target.value)) {
				setMobileNumber(e.target.value);
			}
		}
	};

	const formSubmitHandler = (e: any) => {
		e.preventDefault();

		const leadData = {
			listingID,
			firstName,
			lastName,
			email,
			mobileNumber,
			checkin: dates.checkin,
			checkout: dates.checkout,
			adult,
			children,
		};

		unlockMagicLeadGen.mutate(leadData, {
			onSuccess: () => {
				toast.success("Successfully sent!", {
					position: "top-right",
					autoClose: 2000,
					hideProgressBar: true,
					closeOnClick: false,
					pauseOnHover: false,
					draggable: false,
					progress: undefined,
				});

				setTimeout(() => {
					closeModal();
				}, 2000);
			},
			onError: (err: any) => {
				toast.error(`${err?.response?.data?.error}`, {
					position: "top-right",
					autoClose: 2000,
					hideProgressBar: true,
					closeOnClick: false,
					pauseOnHover: false,
					draggable: false,
					progress: undefined,
				});
			},
		});
	};

	return (
		<>
			<div className="lead-form__container">
				<button
					className="close-btn"
					aria-label="Close"
					onClick={() => closeModal()}
				>
					<SvgClose />
				</button>
				<h2>Reserve Now</h2>
				<form
					className="lead-form__form"
					onSubmit={(e: any) => formSubmitHandler(e)}
				>
					<div className="lead-form__row-wrapper">
						<Input
							type={"text"}
							name={"First Name"}
							placeholder={"First Name"}
							code={null}
							value={firstName}
							onChange={(e: any) => setFirstName(e.target.value)}
						/>
						<Input
							type={"text"}
							name={"Last Name"}
							placeholder={"Last Name"}
							required={false}
							code={null}
							value={lastName}
							onChange={(e: any) => setLastName(e.target.value)}
						/>
					</div>
					<div className="lead-form__row-wrapper">
						<Input
							type={"email"}
							name={"Email"}
							placeholder={"Email"}
							code={null}
							required={false}
							value={email}
							onChange={(e: any) => setEmail(e.target.value)}
						/>
						<Input
							type={"tel"}
							name={"Mobile Number"}
							placeholder={"Mobile Number"}
							code={"+91"}
							value={mobileNumber}
							onChange={(e: any) => handleMobileChange(e)}
						/>
					</div>
					<div className="lead-form__row-wrapper">
						<div className="lead-form__wrapper">
							{dates.checkin !== "" ? <label htmlFor="">Check-in</label> : null}
							<DatePicker
								style={{
									zIndex: 10,
								}}
								noMonth={1}
								open={openCalenderCheckIn}
								setOpen={setOpenCalendarCheckIn}
								dates={dates}
								setDates={setDates}
								mobile={window.innerWidth < 768}
							>
								<input
									inputMode="none"
									onClick={() => {
										setOpenCalendarCheckIn(!openCalenderCheckIn);
									}}
									value={
										dates.checkin !== ""
											? format(dates.checkin, "DD/MM/YYYY")
											: "Click to select check-in"
									}
								/>
							</DatePicker>
						</div>
						<div className="lead-form__wrapper">
							{dates.checkout !== "" ? (
								<label htmlFor="">Check-out</label>
							) : null}
							<DatePicker
								style={{
									zIndex: 10,
								}}
								noMonth={1}
								open={openCalenderCheckOut}
								setOpen={setOpenCalendarCheckOut}
								dates={dates}
								setDates={setDates}
								mobile={window.innerWidth < 768}
							>
								<input
									inputMode="none"
									onClick={() => {
										setOpenCalendarCheckOut(!openCalenderCheckOut);
									}}
									value={
										dates.checkout !== ""
											? format(dates.checkout, "DD/MM/YYYY")
											: "Click to select check-out"
									}
								/>
							</DatePicker>
						</div>
					</div>
					<div className="lead-form__guest-container">
						<p className="lead-form__guest-heading">Number of guests</p>
						<div className="lead-form__guest-wrapper">
							<div className="lead-form__row-wrapper">
								<Adult />
								<p>Adults</p>
							</div>
							<div className="lead-form__guest-input-wrapper">
								<button
									type="button"
									onClick={() => setAdult((prev) => (prev > 1 ? prev - 1 : 1))}
									disabled={adult === 1}
								>
									-
								</button>
								<input type="number" value={adult} readOnly />
								<button
									type="button"
									onClick={() => setAdult((prev) => prev + 1)}
								>
									+
								</button>
							</div>
						</div>
						<div className="lead-form__guest-wrapper">
							<div className="lead-form__row-wrapper">
								<Children />
								<p>Children</p>
							</div>
							<div className="lead-form__guest-input-wrapper">
								<button
									type="button"
									onClick={() =>
										setChildren((prev) => (prev !== 0 ? prev - 1 : 0))
									}
									disabled={children === 0}
								>
									-
								</button>
								<input type="number" value={children} readOnly />
								<button
									type="button"
									onClick={() => setChildren((prev) => prev + 1)}
								>
									+
								</button>
							</div>
						</div>
					</div>
					<button type="submit" className="lead-form__btn-submit">
						Submit
					</button>
				</form>
			</div>
			<ToastContainer />
		</>
	);
};
